import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Shield, Smartphone, Mail, Key, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const TwoFactorSetupPage: React.FC = () => {
  const { companySlug } = useParams<{ companySlug: string }>();
  const navigate = useNavigate();
  const { loginWith2FA, isLoading, error } = useAuth();
  
  const [selectedMethod, setSelectedMethod] = useState<'sms' | 'email' | 'authenticator'>('sms');
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationStep, setVerificationStep] = useState<'setup' | 'verify'>('setup');
  const [qrCode, setQrCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [rememberDevice, setRememberDevice] = useState(false);

  const methods = [
    {
      id: 'sms',
      name: 'SMS',
      description: 'Receive a 6-digit code via text message',
      icon: Smartphone,
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    },
    {
      id: 'email',
      name: 'Email',
      description: 'Receive a 6-digit code via email',
      icon: Mail,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    {
      id: 'authenticator',
      name: 'Authenticator App',
      description: 'Use Google Authenticator, Authy, or similar',
      icon: Key,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100'
    }
  ];

  useEffect(() => {
    if (selectedMethod === 'authenticator') {
      // Mock QR code generation - replace with actual API
      setQrCode('data:image/png;base64,mock_qr_code_data');
    }
  }, [selectedMethod]);

  const handleMethodSelect = (method: 'sms' | 'email' | 'authenticator') => {
    setSelectedMethod(method);
    setVerificationStep('setup');
    setVerificationCode('');
  };

  const handleSetup2FA = async () => {
    if (selectedMethod === 'authenticator') {
      // For authenticator, show QR code and move to verification
      setVerificationStep('verify');
    } else {
      // For SMS/Email, send code and move to verification
      try {
        // Mock sending verification code - replace with actual API
        await new Promise(resolve => setTimeout(resolve, 1000));
        setVerificationStep('verify');
        
        // Mock backup codes generation
        const codes = Array.from({ length: 8 }, () => 
          Math.random().toString(36).substring(2, 8).toUpperCase()
        );
        setBackupCodes(codes);
      } catch (error) {
        console.error('Failed to send verification code:', error);
      }
    }
  };

  const handleVerify2FA = async () => {
    if (!verificationCode.trim()) {
      alert('Please enter the verification code');
      return;
    }

    setIsVerifying(true);
    try {
      await loginWith2FA(verificationCode, selectedMethod, rememberDevice);
      
      // Navigate to dashboard on success
      navigate(`/client/${companySlug}/admin/dashboard`);
    } catch (error) {
      console.error('2FA verification failed:', error);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  const renderSetupStep = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Choose your 2FA method
        </h3>
        <p className="text-sm text-gray-600 mb-6">
          Select how you'd like to receive your verification codes for enhanced security.
        </p>
      </div>

      <div className="space-y-3">
        {methods.map((method) => (
          <div
            key={method.id}
            className={`relative border-2 rounded-lg p-4 cursor-pointer transition-all ${
              selectedMethod === method.id
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => handleMethodSelect(method.id as any)}
          >
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg ${method.bgColor}`}>
                <method.icon className={`h-5 w-5 ${method.color}`} />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-medium text-gray-900">{method.name}</h4>
                <p className="text-sm text-gray-600">{method.description}</p>
              </div>
              {selectedMethod === method.id && (
                <CheckCircle className="h-5 w-5 text-blue-500" />
              )}
            </div>
          </div>
        ))}
      </div>

      {selectedMethod === 'authenticator' && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Setup Instructions</h4>
          <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
            <li>Install Google Authenticator, Authy, or similar app</li>
            <li>Scan the QR code that will appear after setup</li>
            <li>Enter the 6-digit code from your app</li>
          </ol>
        </div>
      )}

              <button
          onClick={handleSetup2FA}
          className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors"
          style={{ backgroundColor: '#0076b5' }}
        >
        Continue with {selectedMethod.toUpperCase()}
      </button>
    </div>
  );

  const renderVerificationStep = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Verify your {selectedMethod.toUpperCase()} setup
        </h3>
        <p className="text-sm text-gray-600">
          {selectedMethod === 'authenticator' 
            ? 'Scan the QR code with your authenticator app and enter the 6-digit code below.'
            : `Enter the 6-digit code sent to your ${selectedMethod === 'sms' ? 'phone' : 'email'}.`
          }
        </p>
      </div>

      {selectedMethod === 'authenticator' && qrCode && (
        <div className="text-center">
          <div className="bg-white p-4 rounded-lg inline-block border">
            <img src={qrCode} alt="QR Code" className="w-32 h-32" />
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Scan this QR code with your authenticator app
          </p>
        </div>
      )}

      <div>
        <label htmlFor="verificationCode" className="block text-sm font-medium text-gray-700 mb-2">
          Verification Code
        </label>
        <input
          id="verificationCode"
          type="text"
          value={verificationCode}
          onChange={(e) => setVerificationCode(e.target.value)}
          className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-lg tracking-widest"
          placeholder="000000"
          maxLength={6}
          autoComplete="one-time-code"
        />
      </div>

      <div className="flex items-center">
        <input
          id="rememberDevice"
          type="checkbox"
          checked={rememberDevice}
          onChange={(e) => setRememberDevice(e.target.checked)}
          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        />
        <label htmlFor="rememberDevice" className="ml-2 block text-sm text-gray-900">
          Remember this device for 30 days
        </label>
      </div>

      {backupCodes.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-yellow-800 mb-2">
            Save Your Backup Codes
          </h4>
          <p className="text-sm text-yellow-700 mb-3">
            Store these codes in a safe place. You can use them to access your account if you lose your 2FA device.
          </p>
          <div className="grid grid-cols-2 gap-2">
            {backupCodes.map((code, index) => (
              <div key={index} className="bg-white px-3 py-2 rounded border font-mono text-sm text-center">
                {code}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex space-x-3">
        <button
          onClick={() => setVerificationStep('setup')}
          className="flex-1 py-3 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
        >
          Back
        </button>
                  <button
            onClick={handleVerify2FA}
            disabled={isVerifying || !verificationCode.trim()}
            className="flex-1 flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            style={{ backgroundColor: '#0076b5' }}
          >
          {isVerifying ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Verifying...
            </div>
          ) : (
            'Verify & Complete Setup'
          )}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-lg flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#0076b5' }}>
            <Shield className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Two-Factor Authentication</h1>
          <p className="text-gray-600">Secure your account with an extra layer of protection</p>
        </div>

        {/* Back to Login */}
        <button
          onClick={handleBackToLogin}
          className="flex items-center text-sm text-blue-600 hover:text-blue-500 mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to login
        </button>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        {verificationStep === 'setup' ? renderSetupStep() : renderVerificationStep()}

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-gray-500">
            Having trouble? Contact{' '}
            <a
              href="mailto:support@vpdmoney.net"
              className="text-blue-600 hover:text-blue-500"
            >
              support@vpdmoney.net
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorSetupPage;
